import { getQueryParameterFromUri } from "@WhiteCap/Common/Utillities/utils";
import {
    SubscribedEmailDto,
    SubscriptionsDashboardState,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import { ApiResultModel as PodApiResultModel } from "@WhiteCap/Store/Service/OsnSubscriptionsServices/PodSubscribersService";
import { Draft } from "immer";

const podSubscriptions = {
    "Custom/SubscriptionDashboard/BeginPodSubscribersDataLoad": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = true;
    },
    "Custom/SubscriptionDashboard/PodSubscribersDataLoadError": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/CompletePodSubscribersDataLoad": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: PodApiResultModel;
        },
    ) => {
        const customerId = getQueryParameterFromUri(action.result?.uri, "customerId");
        const data = {
            properties: action.result.properties,
            subscribedEmails: action.result.subscribedEmails,
            count: action.result.podOrderStatusSummary?.count,
            frequency: action.result.podOrderStatusSummary?.frequency,
        };
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.podOrderStatusSummary = data;
        } else if (draft.osnType === "individualJobSiteLevel" && customerId) {
            draft.jobSiteSubscriptionsById[customerId].podOrderStatusSummary = data;
        }
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/CompleteRemoveSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: string;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.podOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);

            draft.accountLevelSubscriptions.podOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.podOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count: (draft.accountLevelSubscriptions.podOrderStatusSummary?.count || 0) - 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary?.count ||
                        0) - 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/CompleteAddSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: SubscribedEmailDto;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.podOrderStatusSummary?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.accountLevelSubscriptions.podOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.podOrderStatusSummary,
                subscribedEmails: emails,
                count: (draft.accountLevelSubscriptions.podOrderStatusSummary?.count || 0) + 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary
                    ?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary,
                subscribedEmails: emails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary?.count ||
                        0) + 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/CompleteUpdatingSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                customerId: string;
                subscribedEmailDto: SubscribedEmailDto;
            };
        },
    ) => {
        const updatedEmailId = action.result.subscribedEmailDto.email;
        const getUpdatedEmailDtos = (updatedEmailId: string, emailsDto: SubscribedEmailDto[]) => {
            const updatedSubscribedEmails = emailsDto?.map(emailDto => {
                let updatedEmailDto: SubscribedEmailDto = emailDto;
                if (emailDto.email === updatedEmailId) {
                    updatedEmailDto = { ...action.result.subscribedEmailDto };
                }
                return updatedEmailDto;
            });
            return updatedSubscribedEmails;
        };
        if (draft.osnType === "accountLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.accountLevelSubscriptions.podOrderStatusSummary?.subscribedEmails || [],
            );
            draft.accountLevelSubscriptions.podOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.podOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary
                    ?.subscribedEmails || [],
            );
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/CompleteRemovePhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                emailId: string;
            };
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.podOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.accountLevelSubscriptions.podOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.podOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/BeginApplyPodSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = true;
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/ApplyPodSubscriptionsError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/CompleteApplyPodSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/PodSubscriptions/UpdateFrequency": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            frequency: string;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.podOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.podOrderStatusSummary,
                frequency: action.frequency,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].podOrderStatusSummary,
                frequency: action.frequency,
            };
        }
    },
};

export default podSubscriptions;
